// HomePage.js
import React, { useEffect, useContext, useRef } from 'react';
import styled from 'styled-components';
import Header from '../components/Header';
import ActivityWidget from '../components/ActivityWidget';
import MiningSection from '../components/MiningSection';
import ShopSection from '../components/ShopSection';
import InviteFriends from '../components/InviteFriends';
import DailyTasks from '../components/DailyTasks';
import Navigation from '../components/Navigation';
import RegistrationStatus from '../components/RegistrationStatus';
import { TelegramUserContext } from '../App';
import { useUserData } from '../contexts/UserDataContext';

const Container = styled.div`
  width: 100%;
  max-width: 100%;
  margin: 0;
  padding: 0;
  background-color: #ffffff;
  height: 100vh;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  font-size: 13px;
  overflow: hidden;
  position: relative;
  padding-top: 70px; /* Account for fixed header */
  padding-bottom: 80px; /* Account for fixed navigation */
  
  @media (max-width: 375px) {
    font-size: 12px;
    padding-top: 60px;
    padding-bottom: 70px;
  }
`;

const HeaderWrapper = styled.div`
  /* Header is now fixed, no need for wrapper positioning */
`;

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 20px; /* Additional bottom padding for content */
  -webkit-overflow-scrolling: touch;
  position: relative;
  height: calc(100vh - 60px); /* Use viewport height minus header */
  overscroll-behavior: none; /* Prevent pull-to-refresh and overscroll */
  -webkit-overscroll-behavior: none;
  touch-action: pan-y; /* Allow vertical scrolling but prevent pull gestures */
  
  @media (max-width: 375px) {
    height: calc(100vh - 55px);
    padding-bottom: 85px;
  }
`;

const FooterWrapper = styled.div`
  position: fixed;
  bottom: 0;
  z-index: 100;
  width: 100%;
  background-color: white;
  max-width: 100%;
  flex-shrink: 0;
  box-sizing: border-box;
`;

const HomePage = ({ onNavigate, isRegistrationComplete }) => {
  const telegramUser = useContext(TelegramUserContext);
  const { userData, initialLoadUserData } = useUserData();
  
  const hasInitialLoadedRef = useRef(false);
  
  // Load user data only once on initial app load, but wait for registration to complete
  useEffect(() => {
    const loadUserData = async () => {
      // Wait for registration to complete before loading user data
      if (telegramUser?.id && !hasInitialLoadedRef.current && isRegistrationComplete) {
        hasInitialLoadedRef.current = true;
        
        // Always use initialLoadUserData which handles cache internally
        await initialLoadUserData(telegramUser.id);
      }
    };

    loadUserData();
  }, [telegramUser?.id, initialLoadUserData, isRegistrationComplete]); // Added isRegistrationComplete dependency

  return (
    <Container>
      <HeaderWrapper>
        <Header dogeBalance={userData?.balance || 0} pxlBalance={userData?.pxl_balance || 0} />
      </HeaderWrapper>
      <ScrollableContent>
        <ActivityWidget />
        <MiningSection 
          initialUserData={userData}
        />
        <InviteFriends />
        <DailyTasks />
        <ShopSection />
        <RegistrationStatus />
      </ScrollableContent>
      <FooterWrapper>
        <Navigation activePage="home" onNavigate={onNavigate} />
      </FooterWrapper>
    </Container>
  );
};

export default HomePage;
